import { ChangeDetectorRef, Component, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadService } from '../../../service/lead.service';
import { ClevertapService } from '../../../service/clevertap.service';
import { ToastService } from '../../../service/toast.service';
import { CarInsuranceOtpModelComponent } from '../car-insurance-otp-model/car-insurance-otp-model.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { CalenderPickerComponent } from '../calender-picker/calender-picker.component';

@Component({
  selector: 'app-car-insurance-whytochoose',
  standalone: true,
  imports: [CalenderPickerComponent, CarInsuranceOtpModelComponent, TranslatePipe, FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './car-insurance-whytochoose.component.html',
  styleUrl: './car-insurance-whytochoose.component.scss'
})
export class CarInsuranceWhytochooseComponent {
  form!: FormGroup;
  fb = inject(FormBuilder);
  toastService = inject(ToastService);
  leadService = inject(LeadService);
  clevertapService = inject(ClevertapService);
  lang!: string;
  router = inject(Router);
  activeDropdown: string | null = null;
  @ViewChild('phoneInput') phoneInput!: ElementRef;
  selectedBrand: string | null = null;
  showBrandDropdown = false;
  brands = ['Toyota', 'Honda', 'Hyundai', 'Mazda', 'Mitsubishi', 'Others'];
  showCarYearDropdown = false;
  selectedYear: string | null = null;
  years = Array.from({ length: 50 }, (_, i) => (new Date().getFullYear() - i).toString());
  showOtpModal = false;
  otpPhoneMasked = '';
  otpCountryCode = '84';
  otpMobileNo = '';
  otpLoading = false;
  otpErrorMsg = '';
  phoneVerified: boolean = false;
  verifiedPhoneNumber: string = '';
  selectedCountryCode: string = '+84';
  isLoading: boolean = false;
  _actRoute = inject(ActivatedRoute);
  source!: any;

  // ---------- Constructor & Form Setup ----------
  constructor(private elementRef: ElementRef, private route: ActivatedRoute, private cdr: ChangeDetectorRef) {

    // Initialize reactive form with validators
    this.form = this.fb.group({
      name: ['', Validators.required],
      type: ['CAR_INSURANCE'],
      newPhone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      modelName: ['', Validators.required],
      carBrand: ['', Validators.required],
      manufacturingYear: ['', Validators.required],
      insuranceExpiry: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
      consent: [false, Validators.requiredTrue],
      source: ['Website']
    });

  }

  // ngOnInit is a lifecycle hook in Angular
  ngOnInit() {
    this.clevertapService.trackEvent('visited_lead_form', {
      product_type: this.form.get('type')?.value
    });
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    this._actRoute.url.subscribe((urlSegments) => {
      this.source = urlSegments.some((segment) => segment.path === "mobile");
      if (this.source) {
        this.form.get("source")?.setValue("App");
      }
    });
    console.log("Detected source", this.form.get('source')?.value);
    const possibleLang = pathSegments[0];
    const supportedLangs = ['en', 'vi'];
    this.lang = supportedLangs.includes(possibleLang) ? possibleLang : 'en';

    console.log('Detected language from route:', this.lang);

  }
  ngOnChanges() {
    if (this.showOtpModal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  /** Updates the number */
  formatPhone(event: any) {
    let input = event.target.value.replace(/\D/g, '').substring(0, 10);
    const area = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    if (input.length > 6) {
      event.target.value = `(${area}) ${middle}-${last}`;
    } else if (input.length > 3) {
      event.target.value = `(${area}) ${middle}`;
    } else if (input.length > 0) {
      event.target.value = `(${area}`;
    }
    this.form.get('newPhone')?.setValue(input);
    if (!input.startsWith('0')) {
      this.form.get('newPhone')?.setErrors({ invalidPhone: true });
    }
    this.cdr.detectChanges();
  }

  /** Selects a brand and updates the form */
  selectBrand(brand: string) {
    this.selectedBrand = brand;
    this.form.get('carBrand')?.setValue(brand);
    this.showBrandDropdown = false;
    this.toggleBrandDropdown();
  }

  /** Toggles brand dropdown visibility */
  toggleBrandDropdown() {
    this.showBrandDropdown = !this.showBrandDropdown;
    if (this.showBrandDropdown) this.showCarYearDropdown = false;
  }

  // ---------- Car Year Dropdown Handlers ----------
  selectCarYear(year: string, event: MouseEvent) {
    event.stopPropagation();
    this.selectedYear = year;
    this.form.get('manufacturingYear')?.setValue(year);
    this.closeDropdown();
  }

  /** Toggles car year dropdown visibility */
  toggleCarYearDropdown() {
    this.showCarYearDropdown = !this.showCarYearDropdown;
    if (this.showCarYearDropdown) this.showBrandDropdown = false;
  }

  /** Sets insurance expiry date in the form */
  onDateSelected(selectedDate: any) {
    this.form.get('insuranceExpiry')?.setValue(selectedDate);
    this.form.get('insuranceExpiry')?.markAsTouched();
  }

  /** Submits the lead form after validation and triggers phone verification */
  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.form.get('consent')?.value) {
      console.log('Consent is required');
      return;
    }
    this.onVerifyPhone();


  }

  /** Initiates OTP verification for the entered phone number */
  onVerifyPhone() {
    const countryCode = this.selectedCountryCode.replace('+', '') || '84';
    const mobileNo = this.form.get('newPhone')?.value;

    if (!mobileNo || mobileNo.length < 8) {
      this.toastService.error('Validation Error', 'Please enter a valid phone number before requesting OTP.');
      return;
    }

    this.otpCountryCode = countryCode;
    this.otpMobileNo = mobileNo;
    this.otpLoading = true;
    this.otpErrorMsg = '';
    this.showOtpModal = true;

    const payload = { countryCode, mobileNo };
    console.log(payload);

    this.leadService.sendOtp(payload).subscribe({
      next: (response) => {
        this.otpPhoneMasked = mobileNo;
        this.showOtpModal = true;
        this.otpLoading = false;
      },
      error: (err) => {
        this.otpLoading = false;
        this.otpErrorMsg = 'Invalid OTP. Please try again.';
        this.toastService.showApiError(err, this.otpErrorMsg);
        console.error('OTP verification error:', err);
        document.body.style.overflow = 'auto';
      },
    }).add(() => {
      this.otpLoading = false;
      document.body.style.overflow = 'auto';
    });
  }



  /** Allows user to edit the phone number after OTP modal is open */
  onOtpEdit() {
    this.showOtpModal = false;
    document.body.style.overflow = 'auto';
    this.form.get('newPhone')?.enable();
    setTimeout(() => {
      this.phoneInput.nativeElement.focus();
    }, 0);
    this.otpLoading = false;
    this.otpErrorMsg = '';
    this.phoneVerified = false;
    this.verifiedPhoneNumber = '';
  }

  /** Closes OTP modal and resets loading/error states */
  onOtpClosed() {
    this.showOtpModal = false;
    this.otpLoading = false;
    this.otpErrorMsg = '';
    document.body.style.overflow = 'auto';
  }

  /** Dispatches a global event to reset OTP modal */
  resetOtpModal() {
    const resetEvent = new CustomEvent('resetOtpModal');
    window.dispatchEvent(resetEvent);
  }

  /** Handles OTP verification success/failure */
  onOtpVerified(otp: any) {
    this.otpLoading = true;
    this.otpErrorMsg = '';
    const phone = this.otpMobileNo;
    const formatted = phone.replace(/^0/, '');

    const payload = {
      countryCode: this.otpCountryCode,
      mobileNo: formatted,
      otp: otp
    };
    console.log(payload);

    this.leadService.verifyOtp(payload).subscribe({
      next: (response) => {
        this.phoneVerified = true;
        this.verifiedPhoneNumber = this.otpMobileNo;
        this.showOtpModal = false;
        document.body.style.overflow = 'auto';
        if (response) {
          const payload = {
            leadsOptions: [],
            name: this.form?.get('name')?.value,
            mobileNo: this.form?.get('newPhone')?.value,
            countryCode: this.otpCountryCode,
            action: 'Join Now',
            type: this.form.get('type')?.value,
            carBrand: this.form.get('carBrand')?.value,
            carModelNo: this.form.get('modelName')?.value,
            carManufactureYear: this.form.get('manufacturingYear')?.value,
            carInsuranceRenewalDate: this.form.get('insuranceExpiry')?.value,
            source: this.form.get('source')?.value,
          };
          this.isLoading = true;
          this.leadService.submitLead(payload).subscribe({
            next: (response) => {
              this.isLoading = false;
              this.clevertapService.trackEvent('submit_lead_form', { product_type: 'car_insurance' });
              this.router.navigate(['success'], { queryParams: { status: 'car-success' } });
              const queryParams = this.route.snapshot.queryParams;
              this.router.navigate([`${this.lang}/car-insurance/success`], { queryParams }).then(() => {
                window.scrollTo(0, 0);
              });
              this.toastService.success('Success', 'Phone number verified successfully!');
            },
            error: (error) => {
              this.isLoading = false;
              console.error('Error submitting lead:', error);
              this.toastService.error('Error', 'Failed to submit lead. Please try again.');
            },
          }).add(() => {
            this.isLoading = false;
            this.otpLoading = false;
          });
        }
        this.otpLoading = false;
        this.otpErrorMsg = '';
        this.resetOtpModal();
        this.form.reset();
        this.selectedBrand = null;
        this.selectedYear = null;

      },
      error: (err) => {
        this.otpLoading = false;
        console.log("err", this.otpErrorMsg);
        this.otpErrorMsg = err.error?.message || 'OTP verification failed. Please try again.';
        console.error('OTP verification error:', err);
      }
    });
  }

  /** Sends Zalo OTP */
  onOtpZaloVerify() {
    this.otpLoading = true;
    this.otpErrorMsg = '';
    const phone = this.otpMobileNo;
    const formatted = phone.replace(/^0/, '');
    const payload = { countryCode: this.otpCountryCode, mobileNo: formatted };
    console.log(payload);
    this.leadService.sendZaloOtp(payload).subscribe({
      next: (response: any) => {
        this.otpLoading = false;
        this.toastService.showApiSuccess(response, 'Zalo OTP sent successfully!');
      },
      error: (err: any) => {
        this.otpLoading = false;
        this.otpErrorMsg = 'Failed to send Zalo OTP. Please try again.';
        this.toastService.showApiError(err, this.otpErrorMsg);
        console.error('Zalo OTP send error:', err);
      }
    });
  }

  /** Placeholder for Call OTP verification (not implemented yet) */
  onOtpCallVerify() {
    this.otpLoading = false;
    this.toastService.info('Info', 'Call verification is not implemented yet.');
  }
  closeDropdown() {
    this.activeDropdown = null;
  }
  toggleDropdown(name: string) {
    this.activeDropdown = this.activeDropdown === name ? null : name;
  }
  // Listen for clicks on the document to close dropdowns if click is outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (target.closest('.dropdown-container')) {
      return;
    }
    this.closeDropdown();
  }


}
